import React, { useState, useMemo } from 'react';
import { auth } from '../firebase';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RotateCcw, AlertTriangle } from 'lucide-react';

const fmt$ = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

const RefundModal = ({ isOpen, onClose, companyId, invoice, onSuccess }) => {
  const paidTotal = parseFloat(invoice?.amount || invoice?.grandTotal || 0);
  const alreadyRefunded = parseFloat(invoice?.refundedAmount || 0);
  const refundable = Math.max(0, +(paidTotal - alreadyRefunded).toFixed(2));

  const [amount, setAmount]       = useState('');
  const [reason, setReason]       = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refundMethodGuess = useMemo(() => {
    if (!invoice) return null;
    if (invoice.manualMarkPaid && !invoice.stripePaymentIntentId) return 'manual';
    if (invoice.stripePaymentIntentId) return 'stripe';
    return null;
  }, [invoice]);

  const handleFullRefund = () => {
    setAmount(refundable.toFixed(2));
  };

  const numericAmount = parseFloat(amount || 0);
  const overAmount   = numericAmount > refundable + 0.005;
  const tooSmall     = numericAmount < 0.01;
  const canSubmit =
    !submitting &&
    !overAmount &&
    !tooSmall &&
    reason.trim().length > 0 &&
    refundable > 0 &&
    !!refundMethodGuess;

  const handleSubmit = async () => {
    if (!invoice?.invoiceId) return;
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${backendUrl}/api/invoices/${invoice.invoiceId}/refund`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            companyId,
            amount: numericAmount,
            reason: reason.trim(),
          }),
        }
      );
      let data = {};
      try { data = await res.json(); } catch (parseErr) { console.warn(parseErr); }

      if (res.ok && data.success) {
        const fullTxt = data.invoiceStatus === 'refunded' ? ' (full refund)' : '';
        toast.success(`Refund issued — ${fmt$(data.amount)}${fullTxt}`);
        onSuccess?.(data);
        onClose?.();
      } else if (res.status === 403) {
        toast.error(data.detail || 'Refund requires owner or admin role.');
      } else if (res.status === 502) {
        toast.error(data.detail || 'Stripe refund failed.');
      } else {
        toast.error(data.detail || 'Failed to issue refund.');
      }
    } catch (err) {
      console.error('[RefundModal] Network error:', err);
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="refund-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-orange-600" />
            Refund Invoice
          </DialogTitle>
          <DialogDescription>
            {refundMethodGuess === 'stripe'
              ? 'Issues a Stripe refund against the original payment intent. Funds return to the customer\'s card within 5–10 business days.'
              : refundMethodGuess === 'manual'
                ? 'Records an out-of-band refund (e.g. you mailed a check back). No Stripe call is made.'
                : 'This invoice has no recorded payment source — refunds are not available.'}
          </DialogDescription>
        </DialogHeader>

        {/* Invoice summary */}
        <div
          className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
          data-testid="refund-invoice-summary"
        >
          <div className="flex items-center justify-between">
            <p className="font-medium text-sm text-gray-900 truncate">
              {invoice.customerName || 'Unknown customer'}
            </p>
            <p className="text-base font-semibold text-gray-900 shrink-0 ml-3">
              {fmt$(paidTotal)}
            </p>
          </div>
          <div className="flex items-center justify-between text-[11px] text-gray-500 mt-0.5">
            <span className="font-mono">
              #{(invoice.invoiceId || '').slice(-8).toUpperCase()}
            </span>
            <span>
              {alreadyRefunded > 0
                ? <>Already refunded <b className="text-orange-700">{fmt$(alreadyRefunded)}</b> · Refundable <b className="text-gray-900">{fmt$(refundable)}</b></>
                : <>Refundable: <b className="text-gray-900">{fmt$(refundable)}</b></>
              }
            </span>
          </div>
        </div>

        {/* Amount */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="refund-amount" className="text-xs">
              Refund amount <span className="text-red-500">*</span>
            </Label>
            <button
              type="button"
              onClick={handleFullRefund}
              className="text-[11px] text-orange-700 hover:text-orange-800 hover:underline font-medium"
              data-testid="refund-full-shortcut"
            >
              Refund full {fmt$(refundable)}
            </button>
          </div>
          <Input
            id="refund-amount"
            data-testid="refund-amount"
            type="number"
            step="0.01"
            min="0.01"
            max={refundable}
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
          {overAmount && (
            <p
              className="text-[11px] text-red-600 flex items-center gap-1"
              data-testid="refund-amount-over-error"
            >
              <AlertTriangle className="h-3 w-3" />
              Exceeds refundable balance ({fmt$(refundable)}).
            </p>
          )}
        </div>

        {/* Reason */}
        <div className="space-y-1.5">
          <Label htmlFor="refund-reason" className="text-xs">
            Reason / reference <span className="text-red-500">*</span>
          </Label>
          <Input
            id="refund-reason"
            data-testid="refund-reason"
            placeholder="e.g. Service incomplete, customer dispute, check #4421"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
          <p className="text-[11px] text-gray-500">
            Required. Stored on the invoice + transaction history (max 500 chars).
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onClose?.()}
            disabled={submitting}
            data-testid="refund-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="refund-submit-btn"
          >
            {submitting
              ? 'Issuing…'
              : refundMethodGuess === 'stripe'
                ? 'Refund via Stripe'
                : 'Record Refund'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundModal;
